import React, { useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Form } from 'react-bootstrap';
import { ReactComponent as HomeIcon } from '../../shared/icons/home-icon.svg';
import { ReactComponent as FolderIcon } from '../../shared/icons/folder-icon.svg';
import { filterOutFolderAndDescendants } from './folderUtils';
import styles from './FolderTreeSelector.module.scss';

export interface TemplateFolder {
  id: string;
  name: string;
  parentFolder?: {
    id: string;
    name: string;
  } | null;
  childFolders?: TemplateFolder[];
}

export type FolderContext = 'templates' | 'clauses' | 'deals';

interface FolderTreeSelectorProps {
  folders: TemplateFolder[];
  selectedFolderId: string | null;
  onSelectionChange: (folderId: string | null) => void;
  excludeFolderId?: string; // Folder to exclude (e.g., when moving)
  className?: string;
  disabled?: boolean;
  context?: FolderContext; // Context for language (templates vs clauses)
}

interface FolderTreeNode {
  id: string;
  name: string;
  level: number;
  children: FolderTreeNode[];
  isExpanded: boolean;
}

export const FolderTreeSelector: React.FC<FolderTreeSelectorProps> = ({
  folders,
  selectedFolderId,
  onSelectionChange,
  excludeFolderId,
  className,
  disabled = false,
  context = 'templates',
}) => {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  // Build the folder tree structure
  const folderTree = useMemo(() => {
    if (!folders || folders.length === 0) return [];

    // Filter out excluded folder and all its descendants
    const filteredFolders = excludeFolderId ? filterOutFolderAndDescendants(folders, excludeFolderId) : folders;

    // Create a map for quick lookup
    const folderMap = new Map<string, TemplateFolder>();
    filteredFolders.forEach((folder) => {
      folderMap.set(folder.id, folder);
    });

    // Build tree structure
    const rootNodes: FolderTreeNode[] = [];
    const nodeMap = new Map<string, FolderTreeNode>();

    // Create all nodes first
    filteredFolders.forEach((folder) => {
      const node: FolderTreeNode = {
        id: folder.id,
        name: folder.name,
        level: 0,
        children: [],
        isExpanded: expandedFolders.has(folder.id),
      };
      nodeMap.set(folder.id, node);
    });

    // Build parent-child relationships and calculate levels
    filteredFolders.forEach((folder) => {
      const node = nodeMap.get(folder.id);
      if (!node) return;

      if (folder.parentFolder && nodeMap.has(folder.parentFolder.id)) {
        const parentNode = nodeMap.get(folder.parentFolder.id)!;
        parentNode.children.push(node);
        node.level = parentNode.level + 1;
      } else {
        rootNodes.push(node);
      }
    });

    return rootNodes;
  }, [folders, excludeFolderId, expandedFolders]);

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const renderFolderNode = (node: FolderTreeNode): React.ReactNode => {
    const hasChildren = node.children.length > 0;
    const isExpanded = expandedFolders.has(node.id);

    return (
      <div key={node.id} className={styles.folderNode}>
        <div className={styles.folderRow} style={{ paddingLeft: `${node.level * 20 + 8}px` }}>
          <div className={styles.folderContent}>
            {hasChildren && (
              <button
                type="button"
                className={styles.expandButton}
                onClick={() => toggleFolder(node.id)}
                aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
              >
                <span className={`${styles.expandIcon} ${isExpanded ? styles.expanded : ''}`}>▶</span>
              </button>
            )}
            {!hasChildren && <div className={styles.spacer} />}

            <Form.Check
              type="radio"
              id={`folder-${node.id}`}
              name="parentFolder"
              value={node.id}
              checked={selectedFolderId === node.id}
              onChange={() => onSelectionChange(node.id)}
              disabled={disabled}
              className={styles.folderRadio}
            />

            <label htmlFor={`folder-${node.id}`} className={styles.folderLabel}>
              <FolderIcon className={styles.folderIcon} />
              <span className={styles.folderName}>{node.name}</span>
            </label>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className={styles.childrenContainer}>{node.children.map((child) => renderFolderNode(child))}</div>
        )}
      </div>
    );
  };

  return (
    <div className={`${styles.folderTreeSelector} ${className || ''}`}>
      {/* Root folder option */}
      <div className={styles.folderNode}>
        <div className={styles.folderRow}>
          <div className={styles.folderContent}>
            <div className={styles.spacer} />
            <Form.Check
              type="radio"
              id="folder-root"
              name="parentFolder"
              value=""
              checked={selectedFolderId === null}
              onChange={() => onSelectionChange(null)}
              disabled={disabled}
              className={styles.folderRadio}
            />
            <label htmlFor="folder-root" className={styles.folderLabel}>
              <HomeIcon className={styles.homeIcon} />
              <span className={styles.folderName}>
                {context === 'clauses' ? (
                  <FormattedMessage id="clauses.folders.root" defaultMessage="All Clauses (Root)" />
                ) : context === 'deals' ? (
                  <FormattedMessage id="deals.folders.root" defaultMessage="All Deals (Root)" />
                ) : (
                  <FormattedMessage id="templates.folders.root" defaultMessage="All Templates (Root)" />
                )}
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Folder tree */}
      {folderTree.map((node) => renderFolderNode(node))}

      {/* Empty state */}
      {folderTree.length === 0 && (
        <div className={styles.emptyState}>
          {context === 'clauses' ? (
            <FormattedMessage id="clauses.folders.noFolders" defaultMessage="No clause folders available" />
          ) : (
            <FormattedMessage id="templates.folders.noFolders" defaultMessage="No folders available" />
          )}
        </div>
      )}
    </div>
  );
};
